import { markdownAHtml } from "@bilbobot/shared/markdown";
import { useDialogo } from "./dialogo";
import { PieDeRespuesta } from "./PieDeRespuesta";
import { DetalleDeTrabajos } from "./DetalleDeTrabajos";
import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  api,
  ApiError,
  type Adjunto,
  type Conversacion,
  type EstadoDeBilboBot,
  type Mensaje,
  type User,
} from "./api";
import { escucharEventos } from "./api";
import { agrupar, cuandoBreve } from "./fechas";
import { callar, grabar, leer, sePuedeGrabar, trocear, vozEnCola, type Grabacion } from "./voz";
import { manosLibres, sePuedeManosLibres, type ManosLibres } from "./manos-libres";
import { conversar, sePuedeConversar, type Conversacion as CharlaPorVoz } from "./conversacion";
import { modoPedido, tocaHablar, type ModoDeRespuesta } from "@bilbobot/shared/voz";

/**
 * El nombre de una herramienta, dicho como se lo contarías a alguien.
 *
 * `trello_tarjetas` no significa nada para quien espera; «mirando Trello» sí.
 * Lo que no esté en la lista se apaña con el propio nombre, que es mejor que
 * callarse: enseña que está haciendo ALGO, y ese era el punto.
 */
function enMayuscula(texto: string): string {
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

function enCristiano(nombre: string): string {
  const directos: Record<string, string> = {
    leer_web: "leyendo la web",
    analizar_web: "analizando la web",
    seo_de_pagina: "preparando el SEO",
    programar: "encargando el trabajo",
    recordar: "guardando lo que le has dicho",
    consultar_memoria: "buscando en su memoria",
    buscar_en_conversaciones: "repasando conversaciones",
    expediente_cliente: "abriendo la ficha del cliente",
    buscar_clientes: "buscando en la cartera",
    facturacion: "mirando la facturación",
    renovaciones: "mirando las renovaciones",
    leer_correo: "leyendo el correo",
    avisar_por_correo: "escribiendo un correo",
    estado_workers: "mirando los ordenadores",
    consultar_trabajo: "consultando el trabajo",
  };

  if (directos[nombre]) return directos[nombre];
  if (nombre.startsWith("trello_")) return "mirando Trello";
  if (nombre.startsWith("wp_") || nombre.startsWith("cambiar_texto")) return "mirando la web";
  return nombre.replace(/_/g, " ");
}

/**
 * Un mensaje del sistema: cómo acabó un trabajo que se encargó aquí.
 *
 * Se distingue a la vista de lo que escribe el director —no lo ha escrito
 * nadie de la conversación— y la captura se pinta debajo en vez de quedarse en
 * un enlace que hay que abrir. El texto pasa por el mismo `markdownAHtml` que
 * el resto: el título de una página de un cliente es contenido de fuera.
 */
function AvisoDelSistema({ contenido }: { contenido: string }) {
  const capturas = [...contenido.matchAll(/\[(Captura[^\]]*)\]\((\/api\/artefactos\/[\w-]+)\)/g)];
  return (
    <div className="aviso-sistema">
      <span className="de-quien">Aviso del sistema</span>
      <div className="formato" dangerouslySetInnerHTML={{ __html: markdownAHtml(contenido) }} />
      {capturas.map(([, nombre, enlace]) => (
        <a key={enlace} href={enlace} target="_blank" rel="noreferrer" className="captura">
          <img src={enlace} alt={nombre} loading="lazy" />
        </a>
      ))}
    </div>
  );
}

export function Chat({
  usuario,
  historialAbierto,
  cerrarHistorial,
  abrirAlCargar = null,
  pedirAlCargar = null,
}: {
  usuario: User;
  historialAbierto: boolean;
  cerrarHistorial: () => void;
  /** Una conversación pedida por enlace (`?conversacion=`): se abre ésa y no la última. */
  abrirAlCargar?: string | null;
  /** Un encargo de un «Probar» del parte (`?pedir=`): conversación nueva, escrito y sin enviar. */
  pedirAlCargar?: string | null;
}) {
  const { preguntar, pedir, avisar, dialogo } = useDialogo();
  const [conversaciones, setConversaciones] = useState<Conversacion[]>([]);
  const [conversacionId, setConversacionId] = useState<string | null>(null);
  const [mensajes, setMensajes] = useState<Mensaje[]>([]);
  /** Qué aprobación se está decidiendo, para desactivar sus botones. */
  const [decidiendo, setDecidiendo] = useState<string | null>(null);
  const [entrada, setEntrada] = useState("");
  const [respondiendo, setRespondiendo] = useState(false);
  const [pensando, setPensando] = useState(false);
  /**
   * Qué está haciendo ahora mismo y desde cuándo.
   *
   * No hay barra de progreso, y no por falta de ganas: nadie sabe cuánto queda.
   * El modelo no dice por dónde va ni cuánto va a escribir, así que una barra
   * tendría que inventarse el porcentaje — y una barra que se lo inventa es
   * peor que ninguna, porque se cree. Lo que sí es cierto son dos cosas: los
   * segundos que lleva y qué herramienta está usando.
   */
  const [haciendo, setHaciendo] = useState<string | null>(null);
  const [segundos, setSegundos] = useState(0);
  const [error, setError] = useState<string | null>(null);
  /** Se está mirando el archivo en vez de las conversaciones vivas. */
  const [viendoArchivadas, setViendoArchivadas] = useState(false);
  const [renombrando, setRenombrando] = useState<string | null>(null);
  const [nombreNuevo, setNombreNuevo] = useState("");
  /**
   * El turno que está corriendo, para poder cortarlo.
   *
   * En una referencia y no en el estado: cambiarlo no tiene que repintar nada,
   * y `parar` necesita el controlador de VERDAD, no la copia que hubiera en el
   * momento en que se dibujó el botón.
   */
  /** Qué respuesta se acaba de copiar, para confirmarlo un segundo. */
  const [copiado, setCopiado] = useState<string | null>(null);
  /** Cuál se está leyendo en voz alta, para poder pararla. */
  const [leyendo, setLeyendo] = useState<string | null>(null);
  const corte = useRef<AbortController | null>(null);
  const finRef = useRef<HTMLDivElement>(null);
  const hiloRef = useRef<HTMLDivElement>(null);
  /**
   * Si estoy leyendo hacia arriba.
   *
   * El botón de "ir al final" sólo aparece si me he movido: tenerlo siempre
   * ocupa sitio y tapa mensajes para ofrecer algo que ya estás mirando.
   */
  const [heSubido, setHeSubido] = useState(false);
  /** Si el hilo está abajo del todo: sólo entonces se sigue la respuesta. */
  const pegadoAbajo = useRef(true);
  const midiendoScroll = useRef(false);
  /**
   * Las letras que han llegado y aún no se han pintado.
   *
   * El texto llega en trozos muy pequeños y muy seguidos. Repintar la
   * conversación entera con cada uno es trabajo tirado: se juntan y se pintan
   * una vez por fotograma, que es todas las veces que la pantalla puede
   * enseñar.
   */
  const porPintar = useRef("");
  const pintadoPendiente = useRef(false);
  /** Cuándo llegó la última letra. Sirve para saber si el flujo sigue vivo. */
  const ultimaLetra = useRef(0);
  /**
   * Qué mensaje está colocado arriba del todo.
   *
   * Mientras haya uno, nadie mueve el hilo por su cuenta: la respuesta se
   * escribe por debajo y tú decides cuándo bajar.
   */
  const arribaDelTodo = useRef<string | null>(null);

  const [grabacion, setGrabacion] = useState<Grabacion | null>(null);
  /**
   * La grabación también en refs, no solo en estado.
   *
   * Entre que pulsas y que sueltas puede no haber ni un repintado, y el
   * estado de React todavía valdría `null` dentro del manejador. Con refs se
   * lee lo que hay AHORA, que es de lo que depende cerrar el micrófono.
   */
  const grabacionRef = useRef<Grabacion | null>(null);
  /** Si el botón de los auriculares manda aquí. Ver `manos-libres.ts`. */
  const [manosLibresPuesto, setManosLibresPuesto] = useState(false);
  const escucha = useRef<ManosLibres | null>(null);
  /** Lo que se va a mandar con el próximo mensaje. Aún no se ha enviado nada. */
  const [adjuntos, setAdjuntos] = useState<Adjunto[]>([]);
  const [subiendo, setSubiendo] = useState(false);
  const selector = useRef<HTMLInputElement>(null);
  /**
   * La conversación por voz, si está en marcha.
   *
   * Es un bucle largo —escuchar, mandar, oír, escuchar— y vive fuera del
   * estado: lo que hay que consultar dentro del bucle es lo de AHORA, no lo
   * que hubiera cuando se dibujó el botón.
   */
  const charla = useRef<CharlaPorVoz | null>(null);
  const charlando = useRef(false);
  const [enCharla, setEnCharla] = useState(false);
  const [turnosDeCharla, setTurnosDeCharla] = useState(0);
  const [gastoDeCharla, setGastoDeCharla] = useState(0);
  /** Si has llegado a decir algo. Sin esto, el silencio inicial cortaría ya. */
  const hasHablado = useRef(false);
  /** Desde cuándo no se oye nada. */
  const calladoDesde = useRef<number | null>(null);
  /** Para no repetir el aviso de «no te oigo» diez veces por segundo. */
  const avisadoDeMudo = useRef(false);
  const pendienteRef = useRef<Promise<Grabacion> | null>(null);
  const [transcribiendo, setTranscribiendo] = useState(false);
  /** Cuánto llevas hablando. Sin esto no se sabe si está grabando de verdad. */
  const [segundosGrabando, setSegundosGrabando] = useState(0);
  /** Volumen que entra ahora mismo, para pintar la barra. */
  const [nivel, setNivel] = useState(0);
  /**
   * Si el servidor puede transcribir.
   *
   * El dictado necesita clave de OpenAI; la voz de salida no, porque hay la
   * del navegador de reserva. Se pregunta para que el botón no prometa algo
   * que no va a pasar: un micrófono que siempre falla es peor que uno gris
   * que explica por qué.
   */
  const [dictadoDisponible, setDictadoDisponible] = useState<boolean | null>(null);
  /** Con qué cuenta de verdad, para que el saludo no se lo invente. */
  const [estado, setEstado] = useState<EstadoDeBilboBot | null>(null);

  useEffect(() => {
    void api
      .vozDisponible()
      .then((v) => setDictadoDisponible(v.disponible))
      .catch(() => setDictadoDisponible(false));
    void api.estado().then(setEstado).catch(() => setEstado(null));
  }, []);
  /**
   * Cómo contesta: escrito, hablado o las dos cosas.
   *
   * Se guarda en el navegador porque es una preferencia del sitio donde estás,
   * no tuya: en el móvil querrás voz y en el escritorio texto, y tener que
   * cambiarlo cada vez que pasas de uno a otro sería peor que no tenerlo.
   */

  /**
   * Tope de seguridad: a los dos minutos corta y manda lo grabado.
   *
   * Con el dedo en la pantalla no hace falta —sueltas y ya— pero con el botón
   * de los auriculares sí: te olvidas del segundo pellizco, el móvil se queda
   * en el bolsillo y sigue grabando. Y una grabación muy larga acaba
   * rechazándola el servidor, así que se perdería entero lo que dijiste.
   *
   * Corta y MANDA, no cancela: dos minutos de voz son tuyos, y tirarlos por no
   * haber dado un pellizco sería el peor final posible.
   */
  const TOPE_SEGUNDOS = 120;

  useEffect(() => {
    if (!grabacion || segundosGrabando < TOPE_SEGUNDOS) return;
    setError("Dos minutos grabando: lo he cortado y te lo mando. Sigue en otro mensaje si te falta algo.");
    void soltarMicrofono(false);
  }, [grabacion, segundosGrabando]);

  // El contador y la barra de volumen. Se paran solos al soltar.
  useEffect(() => {
    if (!grabacion) return;
    const t = setInterval(() => setSegundosGrabando((s) => s + 1), 1000);
    /**
     * Diez veces por segundo: suficiente para que la barra parezca viva sin
     * repintar el chat entero sesenta veces. Y de paso, para saber cuándo
     * has dejado de hablar.
     */
    const n = setInterval(() => {
      const ahora = grabacion.nivel();
      setNivel(ahora);

      /**
       * Con el auricular, se para al callarse. No con un segundo pellizco.
       *
       * Y no es una preferencia: mientras el micrófono está abierto, el
       * auricular Bluetooth pasa a modo manos libres y **Android le da el botón
       * a la telefonía**, no a la página. La primera pulsación llega porque
       * todavía estamos en modo música; la segunda ya no llega, y no hay nada
       * que una web pueda hacer al respecto. Se probó y esto es lo que hay.
       *
       * Así que el final lo marca la voz: hablas, callas, y se manda. Que
       * además es mejor — no hay que acordarse de nada.
       *
       * Con el dedo en la pantalla no se aplica: ahí soltar ya dice cuándo.
       */
      if (!manosLibresPuesto) return;

      /**
       * Si a los cuatro segundos no ha entrado nada, se dice ya.
       *
       * Sin esto te quedas hablando veintitrés segundos contra un micrófono
       * que no está cogiendo nada y sólo te enteras al final, cuando ya has
       * perdido lo que ibas a decir. Es un aviso, no un corte: puede que estés
       * pensando, así que sigue grabando por si acaso.
       */
      if (!hasHablado.current && grabacion.duracionMs() > 4000 && !avisadoDeMudo.current) {
        avisadoDeMudo.current = true;
        setError(
          "No te estoy oyendo. Si llevas los auriculares puestos, puede que su micrófono no esté disponible para el navegador: prueba a hablarle al móvil, o quita el manos libres y usa el botón con el dedo.",
        );
      }

      if (ahora > UMBRAL_VOZ) {
        hasHablado.current = true;
        calladoDesde.current = null;
        return;
      }

      // El silencio de antes de empezar no cuenta: se piensa antes de hablar.
      if (!hasHablado.current) return;

      calladoDesde.current ??= Date.now();
      if (Date.now() - calladoDesde.current > SILENCIO_MS) void soltarMicrofono(false);
    }, 100);
    return () => {
      clearInterval(t);
      clearInterval(n);
    };
  }, [grabacion]);


  // Al abrir, se retoma la última conversación en vez de empezar de cero:
  // BilboBot debe sentirse como una sola identidad continua (§44), no como
  // alguien que olvida cada vez que recargas la página.
  useEffect(() => {
    void (async () => {
      const { conversations } = await api.conversaciones();
      setConversaciones(conversations);

      /*
       * Si se llega por un enlace —«viene de» en una aprobación—, se abre ESA,
       * aunque esté archivada y no salga en la lista: es la que tiene el
       * contexto para decidir.
       */
      /*
       * Un encargo que llega del parte va en una conversación nueva: metido en la
       * última, el director lo leería con el hilo de otra cosa delante.
       */
      const ultima = pedirAlCargar ? undefined : abrirAlCargar ? { id: abrirAlCargar } : conversations[0];
      if (pedirAlCargar) setEntrada(pedirAlCargar);
      if (ultima) {
        setConversacionId(ultima.id);
        const { messages } = await api.mensajes(ultima.id);
        setMensajes(messages);
      } else {
        const { conversation } = await api.crearConversacion();
        setConversacionId(conversation.id);
        /* Delante de las que hubiera: con un encargo del parte, la lista no está vacía. */
        setConversaciones((previas) => [
          { ...conversation, updatedAt: new Date().toISOString(), mensajes: 0 },
          ...previas,
        ]);
      }
    })();
  }, []);

  /**
   * Seguir la respuesta según se escribe, sin pelearse con el dedo.
   *
   * Esto era `scrollIntoView({ behavior: "smooth" })` con `mensajes` en las
   * dependencias, y `mensajes` cambia con CADA LETRA que llega. O sea: una
   * animación de desplazamiento nueva por letra, cada una cancelando la
   * anterior a media curva. En el portátil se disimula; en el móvil es
   * exactamente la sensación de que la pantalla se traba.
   *
   * Mientras escribe se salta seco —que es lo que se quiere: seguir el texto—
   * y sólo se anima cuando el salto es de verdad, al abrir una conversación o
   * al mandar un mensaje.
   *
   * Y si se ha subido a releer algo, no se baja: arrastrar a alguien al final
   * mientras lee es peor que no seguir la respuesta.
   */
  /**
   * El contador de segundos, mientras espera.
   *
   * Un segundo de resolución: lo que se quiere saber es si lleva cinco
   * segundos o cincuenta, no la centésima. Y se para al terminar, que si no
   * seguiría contando y gastando pintados para nada.
   */
  useEffect(() => {
    if (!respondiendo) {
      setSegundos(0);
      setHaciendo(null);
      return;
    }
    const desde = Date.now();
    const reloj = setInterval(() => setSegundos(Math.round((Date.now() - desde) / 1000)), 1000);
    return () => clearInterval(reloj);
  }, [respondiendo]);

  /**
   * Ir al final sólo al abrir una conversación.
   *
   * Mientras escribe NO se mueve nada: el mensaje se colocó arriba al mandarlo
   * y el texto va apareciendo por debajo, dentro de la pantalla. Perseguir el
   * final mientras llega texto era lo que dejaba la respuesta pegada al borde
   * de abajo y obligaba a subir para empezar a leerla.
   */
  useEffect(() => {
    if (respondiendo || arribaDelTodo.current) return;
    if (!pegadoAbajo.current) return;
    finRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [mensajes, pensando, respondiendo]);

  /**
   * Recoger la respuesta cuando el móvil ha cortado la conexión.
   *
   * Al minimizar el navegador para ir a WhatsApp, el móvil cierra la conexión
   * abierta. El servidor ya no lo trata como «para», así que termina el turno y
   * lo guarda — pero esta pantalla se queda mirando un flujo muerto y con el
   * texto a medias para siempre.
   *
   * Al volver se comprueba si sigue llegando algo. Si no llega, se pide la
   * conversación al servidor hasta que aparezca la respuesta terminada. No es
   * un apaño: es la única forma de saberlo, porque el flujo roto no avisa de
   * que se ha roto.
   */
  useEffect(() => {
    if (!respondiendo || !conversacionId) return;

    let vivo = true;
    let espera: ReturnType<typeof setTimeout> | undefined;

    async function rescatar(intento: number) {
      if (!vivo || intento > 24) return;
      try {
        const { messages } = await api.mensajes(conversacionId!);
        if (!vivo) return;

        // Se adopta cuando el servidor ya tiene la respuesta escrita: hasta
        // entonces el último mensaje sigue siendo el que mandaste tú.
        if (messages.at(-1)?.role === "assistant") {
          corte.current = null;
          porPintar.current = "";
          setMensajes(messages);
          setRespondiendo(false);
          setPensando(false);
          return;
        }
      } catch {
        /* se reintenta */
      }
      espera = setTimeout(() => void rescatar(intento + 1), 2_500);
    }

    function alVolver() {
      if (document.visibilityState !== "visible") return;
      // Si el texto sigue llegando, el flujo está vivo y no hay nada que hacer.
      if (Date.now() - ultimaLetra.current < 5_000) return;
      void rescatar(0);
    }

    document.addEventListener("visibilitychange", alVolver);
    return () => {
      vivo = false;
      clearTimeout(espera);
      document.removeEventListener("visibilitychange", alVolver);
    };
  }, [respondiendo, conversacionId]);

  /**
   * Lo que llega sin haberlo pedido: el resultado de un trabajo que terminó
   * cuando ya no había turno. Si es de la conversación abierta, se pinta al
   * final; si es de otra, sube en la lista para que se vea que hay algo.
   */
  useEffect(
    () =>
      escucharEventos({
        onMensaje: (cid, mensaje) => {
          if (cid === conversacionId) {
            setMensajes((previos) =>
              previos.some((m) => m.id === mensaje.id) ? previos : [...previos, mensaje],
            );
          }
          setConversaciones((previas) => {
            const actualizada = previas.map((c) =>
              c.id === cid
                ? { ...c, updatedAt: new Date().toISOString(), mensajes: c.mensajes + 1 }
                : c,
            );
            const esta = actualizada.find((c) => c.id === cid);
            return esta ? [esta, ...actualizada.filter((c) => c.id !== cid)] : actualizada;
          });
        },
      }),
    [conversacionId],
  );

  /**
   * Al elegir una conversación, el panel se va.
   *
   * Ahora tapa el chat en las dos pantallas, así que dejarlo abierto sería
   * dejar tapado justo lo que acabas de pedir.
   */
  /**
   * Se anota si me he movido del final.
   *
   * El margen de 120 px evita que un rebote del navegador al llegar abajo
   * encienda el botón medio segundo y lo apague: parpadear es peor que no estar.
   */
  function alDesplazar() {
    /**
     * Una medida por fotograma, no una por evento.
     *
     * Leer `scrollHeight` obliga al navegador a recalcular la página entera, y
     * un dedo arrastrando dispara ese evento decenas de veces por segundo. Con
     * el chat lleno de burbujas eso es medio segundo de tirones cada vez que se
     * sube a releer algo.
     */
    if (midiendoScroll.current) return;
    midiendoScroll.current = true;

    requestAnimationFrame(() => {
      midiendoScroll.current = false;
      const h = hiloRef.current;
      if (!h) return;
      const lejos = h.scrollHeight - h.scrollTop - h.clientHeight > 120;
      pegadoAbajo.current = !lejos;
      setHeSubido((antes) => (antes === lejos ? antes : lejos));
    });
  }

  function irAlPrincipio() {
    hiloRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }

  function irAlFinal() {
    finRef.current?.scrollIntoView({ behavior: "smooth" });
  }

  function cerrarSiTapa() {
    cerrarHistorial();
  }

  async function abrir(id: string) {
    if (respondiendo || id === conversacionId) return;
    setConversacionId(id);
    setError(null);
    cerrarSiTapa();
    const { messages } = await api.mensajes(id);
    // Otra conversación se abre por el final, como siempre: ahí lo que quieres
    // es lo último que se dijo, no lo primero.
    arribaDelTodo.current = null;
    setMensajes(messages);
  }

  async function verArchivadas(archivadas: boolean) {
    setViendoArchivadas(archivadas);
    setRenombrando(null);
    const { conversations } = await api.conversaciones(archivadas);
    setConversaciones(conversations);
  }

  function empezarARenombrar(c: Conversacion) {
    setRenombrando(c.id);
    setNombreNuevo(c.title);
  }

  async function guardarNombre(evento: FormEvent) {
    evento.preventDefault();
    const id = renombrando;
    const titulo = nombreNuevo.trim();
    setRenombrando(null);
    if (!id || !titulo) return;

    setConversaciones((previas) =>
      previas.map((c) => (c.id === id ? { ...c, title: titulo } : c)),
    );
    try {
      await api.renombrar(id, titulo);
    } catch {
      // Si el servidor lo rechaza, se recarga la lista en vez de dejar en
      // pantalla un nombre que no existe en la base de datos.
      await verArchivadas(viendoArchivadas);
      setError("No se pudo renombrar.");
    }
  }

  /** Archivar la saca de la lista; desarchivar la devuelve. Nada se pierde. */
  async function archivar(c: Conversacion) {
    setConversaciones((previas) => previas.filter((x) => x.id !== c.id));
    await api.archivar(c.id, !viendoArchivadas);
    if (c.id === conversacionId) {
      setConversacionId(null);
      setMensajes([]);
    }
  }

  async function borrar(c: Conversacion) {
    /**
     * Preguntar diciendo cuánto se pierde.
     *
     * "¿Seguro?" no es una pregunta: no aporta nada que decidir. El número de
     * mensajes sí — es la diferencia entre tirar una conversación de dos
     * líneas y tirar el registro entero de un mes de trabajo, que además es
     * lo único donde el director puede buscar después.
     */
    const aviso =
      `Se va a borrar «${c.title}» y sus ${c.mensajes} mensajes.\n\n` +
      "Esto no se puede deshacer, y el director dejará de poder buscar en ellos.\n" +
      "Si solo quieres quitarla de la lista, usa Archivar.";
    if (!await preguntar(aviso)) return;

    setConversaciones((previas) => previas.filter((x) => x.id !== c.id));
    await api.borrarConversacion(c.id);
    if (c.id === conversacionId) {
      setConversacionId(null);
      setMensajes([]);
    }
  }

  async function nueva() {
    if (respondiendo) return;
    const { conversation } = await api.crearConversacion();
    setConversacionId(conversation.id);
    setMensajes([]);
    setError(null);
    cerrarSiTapa();

    // Una conversación nueva nace viva. Si estabas mirando el archivo hay que
    // traerse la lista de las vivas: prependerla a la lista archivada la
    // pondría entre conversaciones que no están donde ella.
    const listaBase = viendoArchivadas
      ? (await api.conversaciones(false)).conversations
      : conversaciones;
    setViendoArchivadas(false);
    setConversaciones([
      { ...conversation, updatedAt: new Date().toISOString(), mensajes: 0 },
      ...listaBase,
    ]);
  }

  /**
   * Nota de voz como en WhatsApp: mantener pulsado, soltar y va.
   *
   * La primera versión era pulsar, hablar, volver a pulsar, y dejaba lo
   * transcrito en la caja para revisarlo. Más seguro sobre el papel, pero no
   * es como habla nadie con el móvil en la mano: se mantiene el dedo, se
   * suelta y se manda.
   *
   * El seguro contra transcripciones malas no desaparece, cambia de sitio: lo
   * que se envía aparece como tu mensaje en la pantalla, así que se ve lo que
   * ha entendido. Y arrastrando el dedo fuera del botón se cancela sin enviar
   * nada, que es la salida de emergencia de WhatsApp.
   */
  /**
   * Una pulsación del botón: si no está grabando empieza, y si está, manda.
   *
   * Es la diferencia con el dedo en la pantalla: ahí mantienes y sueltas, aquí
   * no hay nada que mantener, así que la misma tecla hace las dos cosas.
   *
   * Va por referencia y no como función normal porque quien la llama es el
   * navegador desde la sesión de medios, y ese manejador se registró una sola
   * vez: si mirara el estado, miraría el de cuando se encendió.
   */
  const alternarGrabacion = useRef(() => {});
  alternarGrabacion.current = () => {
    if (grabacionRef.current) void soltarMicrofono(false);
    else void empezarAGrabar();
  };

  async function encenderManosLibres() {
    try {
      escucha.current = await manosLibres(() => alternarGrabacion.current());
      setManosLibresPuesto(true);
    } catch {
      setError(
        "No se pudo encender el manos libres: el navegador no ha dejado sonar el tono que hace falta para escuchar el botón. Toca la pantalla una vez y vuelve a darle.",
      );
    }
  }

  function apagarManosLibres() {
    escucha.current?.parar();
    escucha.current = null;
    setManosLibresPuesto(false);
  }

  // Al salir de la pantalla se suelta el botón: dejarlo secuestrado sería
  // quitarle el mando de la música a alguien que ya no está aquí.
  useEffect(() => () => escucha.current?.parar(), []);

  async function elegirFicheros(lista: FileList | null) {
    if (!lista?.length) return;
    setSubiendo(true);
    setError(null);
    try {
      for (const fichero of Array.from(lista).slice(0, 5)) {
        try {
          const { adjunto, aviso } = await api.subirAdjunto(fichero);
          setAdjuntos((previos) => [...previos, adjunto]);
          // El aviso no impide adjuntarlo: un PDF escaneado se puede guardar
          // igual, lo que no se puede es dar por hecho que se ha leído.
          if (aviso) setError(aviso);
        } catch (e) {
          setError(e instanceof Error ? e.message : "No se pudo subir el fichero.");
        }
      }
    } finally {
      setSubiendo(false);
      // Sin esto, elegir DOS VECES el mismo fichero no dispara el evento: el
      // selector cree que no ha cambiado nada.
      if (selector.current) selector.current.value = "";
    }
  }

  /**
   * Los topes de una conversación. **Guardia 2.**
   *
   * No están por el precio —cuatro céntimos no arruinan a nadie— sino porque un
   * bucle que se dispara solo no se para solo. Oye ruido, contesta, se oye a sí
   * mismo, contesta otra vez. Con el móvil en el bolsillo eso no se nota hasta
   * que lo sacas.
   */
  const TOPE_TURNOS = 60;
  const TOPE_CENTIMOS = 30;

  /**
   * El bucle: escuchar, mandar, oír la respuesta, volver a escuchar.
   *
   * Cada vuelta hace lo mismo que harías tú a mano —dictar, leer, contestar—
   * pero sin tocar la pantalla. Lo único que no es obvio es el orden del final:
   * se silencia el micrófono ANTES de que empiece a hablar y se reanuda DESPUÉS
   * de que termine, que es lo que impide que se conteste a sí mismo.
   */
  async function bucleDeCharla(id: string) {
    let turnos = 0;
    let centimos = 0;

    while (charlando.current) {
      const audio = await charla.current?.escuchar();
      if (!audio || !charlando.current) break;

      let dicho = "";
      try {
        dicho = (await api.transcribir(id, audio)).trim();
      } catch {
        continue;
      }
      // Ruido que sonó a voz pero no dijo nada. Ni se manda ni se paga.
      if (!dicho) continue;

      /**
       * Se escribe todo: lo tuyo y lo suyo.
       *
       * Estuvo un rato sin pintar la respuesta —la estás escuchando, para qué
       * llenar el hilo— y se quitó porque pintarla no cuesta NADA: el texto ya
       * existe, esos tokens se pagan al generarlos, y dibujarlo pasa entero en
       * el móvil. Ocultarlo no ahorraba un céntimo.
       *
       * Y poder leerlo vale: se oye una frase a medias, se quiere repasar un
       * número, se vuelve dos horas después. Escuchar y leer no compiten.
       */
      setMensajes((previos) => [
        ...previos,
        { id: `local-${Date.now()}`, role: "user", content: dicho },
        { id: "respondiendo", role: "assistant", content: "" },
      ]);
      setRespondiendo(true);

      /**
       * Empieza a hablar en cuanto hay una frase, no cuando hay una respuesta.
       *
       * Es lo que más se nota de toda la latencia. Esperar a que el modelo
       * termine y sólo entonces generar el audio son dos esperas seguidas: con
       * una respuesta de cuatro líneas, cuatro segundos mirando el techo.
       *
       * Así la primera frase se está generando mientras el modelo escribe la
       * segunda, y a partir de ahí ya no hay silencios. La conversación empieza
       * cuando él tiene algo que decir, no cuando lo ha dicho todo.
       */
      const voz = vozEnCola();
      // Guardia 3: se silencia ANTES de la primera palabra, no después.
      charla.current?.silenciar();

      let porDecir = "";

      try {
        await api.enviar(
          id,
          dicho,
          {
            onText: (t) => {
              porPintar.current += t;
              setMensajes((previos) =>
                previos.map((m) =>
                  m.id === "respondiendo"
                    ? { ...m, content: m.content + porPintar.current }
                    : m,
                ),
              );
              porPintar.current = "";

              /**
               * Se le echa a la voz por frases enteras.
               *
               * Cortar donde caiga sonaría a corte de emisión: la entonación se
               * queda colgada a mitad. `trocear` corta siempre en final de
               * frase, y lo que no llega a frase espera al trozo siguiente.
               */
              porDecir += t;
              const frases = trocear(porDecir, 90, 240);
              // La última puede estar a medias: se queda esperando.
              while (frases.length > 1) voz.decir(frases.shift()!);
              porDecir = frases[0] ?? "";
              if (/[.!?…]\s*$/.test(porDecir)) {
                voz.decir(porDecir);
                porDecir = "";
              }
            },
            onDone: ({ costCents }) => {
              centimos += costCents;
              setMensajes((previos) =>
                previos.map((m) =>
                  m.id === "respondiendo" ? { ...m, id: `msg-${Date.now()}` } : m,
                ),
              );
            },
            onError: (mensaje) => {
              setError(mensaje);
              charlando.current = false;
            },
          },
          "voice",
        );
      } catch {
        charlando.current = false;
      }

      setRespondiendo(false);

      // Lo que quedó sin punto final también se dice.
      if (porDecir.trim()) voz.decir(porDecir);
      if (!charlando.current) {
        voz.cortar();
        break;
      }

      await voz.fin();
      charla.current?.reanudar();

      turnos++;
      setTurnosDeCharla(turnos);
      setGastoDeCharla(centimos);

      if (turnos >= TOPE_TURNOS || centimos >= TOPE_CENTIMOS) {
        setError(
          `He parado la conversación: ${turnos} intercambios y ${centimos.toFixed(1)} céntimos. Vuelve a darle si quieres seguir.`,
        );
        break;
      }
    }

    colgarCharla();
  }

  async function empezarCharla() {
    if (!conversacionId) return;
    setError(null);
    try {
      charla.current = await conversar();
      charlando.current = true;
      setEnCharla(true);
      setTurnosDeCharla(0);
      setGastoDeCharla(0);
      void bucleDeCharla(conversacionId);
    } catch {
      setError("No se pudo abrir el micrófono para conversar. Comprueba el permiso del navegador.");
    }
  }

  function colgarCharla() {
    charlando.current = false;
    charla.current?.colgar();
    charla.current = null;
    setEnCharla(false);
    callar();

    /**
     * Y se recarga el hilo, que es donde estaba todo lo que no se pintó.
     *
     * Mientras se habla el chat se queda limpio, pero lo dicho se ha ido
     * guardando igual. Al colgar aparece por escrito, que es cuando vuelves a
     * mirar la pantalla y cuando de verdad hace falta poder releerlo.
     */
    const id = conversacionId;
    if (id) {
      void api
        .mensajes(id)
        .then(({ messages }) => setMensajes(messages))
        .catch(() => {});
    }
  }

  // Al salir de la pantalla se cuelga: un micrófono abierto que nadie mira es
  // el punto rojo encendido en el móvil hasta que se recarga.
  useEffect(() => () => colgarCharla(), []);

  async function empezarAGrabar() {
    if (grabacionRef.current || respondiendo || transcribiendo || !conversacionId) return;
    if (dictadoDisponible === false) return;

    setError(null);
    setSegundosGrabando(0);
    hasHablado.current = false;
    calladoDesde.current = null;
    avisadoDeMudo.current = false;

    /**
     * Abrir el micrófono tarda, y hay que contar con que sueltes antes.
     *
     * `getUserMedia` puede tardar medio segundo largo —más la primera vez,
     * con el permiso de por medio—. Antes, si soltabas en ese hueco, el
     * `pointerup` no encontraba ninguna grabación y se iba sin hacer nada…
     * y un instante después el micrófono se abría y se quedaba abierto para
     * siempre, con el punto rojo encendido en el móvil hasta recargar.
     *
     * Ahora la promesa se guarda antes de esperarla, y al soltar se espera a
     * que termine de abrirse para cerrarla en condiciones.
     */
    /**
     * El tono se calla ANTES de abrir el micrófono, y se le da un respiro.
     *
     * Un auricular Bluetooth no reproduce y graba a la vez en condiciones: al
     * abrir el micrófono, Android cambia el perfil del aparato. Con el tono
     * sonando encima, la grabación salía muda —quince segundos de silencio— y
     * el cambio de perfil tampoco terminaba de asentarse. Los 200 ms son para
     * que le dé tiempo.
     */
    if (escucha.current) {
      escucha.current.micAbierto();
      await new Promise((listo) => setTimeout(listo, 200));
    }

    const promesa = grabar();
    pendienteRef.current = promesa;

    try {
      const nueva = await promesa;
      if (pendienteRef.current !== promesa) {
        // Se soltó mientras se abría: ya no la quiere nadie.
        nueva.cancelar();
        return;
      }
      grabacionRef.current = nueva;
      setGrabacion(nueva);
    } catch {
      pendienteRef.current = null;
      setError(
        "No se ha podido abrir el micrófono. Comprueba que le has dado permiso al navegador.",
      );
    }
  }

  /** Menos de esto es un roce, no una nota de voz. */
  const MINIMO_MS = 700;

  /**
   * A partir de qué volumen se considera que estás hablando.
   *
   * Bajo a propósito. Pasarse por arriba corta a alguien que habla flojo a
   * media frase, que es mucho peor que aguantar dos segundos de más.
   */
  const UMBRAL_VOZ = 0.06;

  /**
   * Cuánto silencio hace falta para dar por terminada la frase.
   *
   * Tres segundos, no dos. Al hablar se piensa a mitad —se busca un nombre, se
   * recuerda una cifra— y cortar ahí parte el mensaje en dos. Esperar un
   * segundo de más no le cuesta nada a nadie; que te corten mientras piensas
   * te obliga a repetirlo entero.
   */
  const SILENCIO_MS = 3000;

  /**
   * ¿Se ha soltado lejos del botón? Entonces era para cancelar.
   *
   * Con el puntero capturado, `pointerleave` ya no salta, así que la salida
   * de emergencia de WhatsApp —arrastrar fuera— se mide a mano. Se deja un
   * margen generoso: el dedo se mueve solo al hablar, y cancelar sin querer
   * una nota larga enfada más que enviar una que no querías.
   */
  function fueraDelBoton(e: { clientX: number; clientY: number; currentTarget: Element }) {
    const caja = e.currentTarget.getBoundingClientRect();
    const margen = 70;
    return (
      e.clientX < caja.left - margen ||
      e.clientX > caja.right + margen ||
      e.clientY < caja.top - margen ||
      e.clientY > caja.bottom + margen
    );
  }

  async function soltarMicrofono(cancelado: boolean) {
    const promesa = pendienteRef.current;
    if (!promesa) return;
    pendienteRef.current = null;

    // El tono vuelve en cuanto se suelta el micrófono, pase lo que pase con la
    // grabación: si no volviera, la siguiente pulsación no llegaría aquí.
    escucha.current?.micCerrado();

    // Si todavía se estaba abriendo, se espera: es la única forma de cerrarla.
    const grabada = grabacionRef.current ?? (await promesa.catch(() => null));
    grabacionRef.current = null;
    setGrabacion(null);
    if (!grabada) return;

    const duracion = grabada.duracionMs();
    const maximo = grabada.nivelMaximo();

    if (cancelado || duracion < MINIMO_MS) {
      grabada.cancelar();
      if (!cancelado) {
        setError("Muy corto. Mantén pulsado el micrófono mientras hablas.");
      }
      return;
    }

    setTranscribiendo(true);
    // Fuera del try: si algo falla, el mensaje de error necesita estos datos
    // para decir qué se grabó, y dentro del bloque no existirían.
    let audio: Blob | null = null;
    try {
      audio = await grabada.parar();

      /**
       * Distinguir "no entró sonido" de "no se entendió".
       *
       * Son dos averías distintas y llevan a mirar sitios distintos: la
       * primera es el micrófono de este aparato, la segunda es la
       * transcripción. Con un solo mensaje para las dos se busca donde no es.
       */
      if (maximo < 0.02) {
        setError(
          `El micrófono no captó nada (${(duracion / 1000).toFixed(1)} s de silencio). ` +
            "Mira si el navegador está usando otro micrófono, o si está silenciado.",
        );
        return;
      }

      if (audio.size < 1_200) {
        setError(
          `Se oyó sonido pero la grabación salió vacía (${audio.size} bytes). ` +
            "Puede ser cosa de este navegador: prueba en otro.",
        );
        return;
      }

      const texto = await api.transcribir(conversacionId!, audio);
      if (!texto.trim()) {
        setError(
          `Se grabaron ${(duracion / 1000).toFixed(1)} s con sonido ` +
            `(${Math.round(audio.size / 1024)} KB) pero no se entendió nada. ` +
            "Prueba hablando más cerca y más despacio.",
        );
        return;
      }
      await enviarTexto(texto);
    } catch (e) {
      const kb = audio ? `${Math.round(audio.size / 1024)} KB` : "sin grabación";
      const detalle = `${(duracion / 1000).toFixed(1)} s · ${kb} · nivel máx ${maximo.toFixed(2)}`;
      setError(
        `${e instanceof ApiError ? e.message : "No se pudo transcribir la nota."} (${detalle})`,
      );
    } finally {
      setTranscribiendo(false);
    }
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    void enviarTexto(entrada);
  }

  /**
   * Copia una respuesta al portapapeles.
   *
   * Se copia el texto TAL CUAL, con su markdown. Es lo que quieres para pegarlo
   * en un documento, y quitarle las marcas aquí sería decidir por ti: si lo que
   * necesitas es texto pelado, se quita al pegar; si te lo quito yo, no hay
   * forma de recuperarlo.
   */
  async function copiar(mensajeId: string, texto: string) {
    try {
      await navigator.clipboard.writeText(texto);
    } catch {
      /**
       * Sin permiso de portapapeles, se selecciona.
       *
       * Pasa en navegadores viejos y sobre conexiones sin cifrar. Dejar el
       * texto seleccionado permite copiarlo a mano, que es mejor que un botón
       * que no hace nada y no dice por qué.
       */
      const nodo = document.getElementById(`msg-${mensajeId}`);
      if (nodo) {
        const rango = document.createRange();
        rango.selectNodeContents(nodo);
        const seleccion = window.getSelection();
        seleccion?.removeAllRanges();
        seleccion?.addRange(rango);
      }
      return;
    }

    setCopiado(mensajeId);
    // Se quita solo: un "Copiado" que se queda ahí deja de significar nada.
    setTimeout(() => setCopiado((actual) => (actual === mensajeId ? null : actual)), 1_600);
  }

  /** Lee una respuesta, o la calla si ya está sonando. */
  function escuchar(mensajeId: string, texto: string) {
    if (leyendo === mensajeId) {
      callar();
      setLeyendo(null);
      return;
    }

    callar();
    setLeyendo(mensajeId);
    void leer(texto).finally(() => {
      setLeyendo((actual) => (actual === mensajeId ? null : actual));
    });
  }

  /**
   * Corta lo que esté haciendo ahora mismo.
   *
   * Abortar cierra la conexión, y el servidor lo nota: deja de llamar al
   * modelo y —lo que más importa— no ejecuta ninguna herramienta más. Media
   * respuesta de más son tokens; una tarjeta de Trello creada después de
   * pulsar parar es trabajo que alguien tiene que deshacer.
   *
   * Lo escrito hasta ese momento se queda en pantalla. Es tuyo y puede valer:
   * borrarlo sería castigarte por haber parado.
   */
  function parar() {
    /**
     * Se dice, y además se cuelga.
     *
     * La petición es lo que de verdad para el turno al otro lado: cerrar la
     * conexión ya no basta, y eso es a propósito — un móvil la cierra solo al
     * minimizar y eso no es parar. El abort local es para que la pantalla
     * responda en el acto sin esperar al viaje de ida.
     */
    if (conversacionId) {
      void fetch(`/api/conversations/${conversacionId}/parar`, {
        method: "POST",
        credentials: "same-origin",
      }).catch(() => {});
    }
    corte.current?.abort();
    corte.current = null;
    setRespondiendo(false);
    setPensando(false);
    setMensajes((previos) =>
      previos
        .map((m) =>
          m.id === "respondiendo"
            ? { ...m, id: `parado-${Date.now()}`, content: m.content.trimEnd() }
            : m,
        )
        .filter((m) => m.content.trim() !== ""),
    );
  }

  /**
   * Manda un mensaje, venga escrito o dictado.
   *
   * Un solo camino a propósito: lo que se dicta entra exactamente por donde
   * entra lo que se teclea. Si fueran dos, habría dos sitios donde arreglar
   * cada cosa y uno de los dos se quedaría atrás.
   */

  /**
   * Aprobar o denegar desde la tarjeta del chat.
   *
   * Mismo endpoint que la pestaña Hoy — decidir por dos puertas sería decidir
   * distinto — y la tarjeta se voltea al momento con la respuesta, sin
   * recargar: el estado nuevo se escribe sobre el mensaje que la trae.
   */
  async function decidirAprobacion(id: string, aprobada: boolean) {
    if (aprobada && !await preguntar("¿Aprobar? Se ejecuta en cuanto digas que sí.")) return;
    let nota: string | undefined;
    if (!aprobada) {
      const respuesta = await pedir("¿Por qué no? (opcional, se guarda)");
      if (respuesta === null) return;
      nota = respuesta || undefined;
    }

    setDecidiendo(id);
    try {
      await api.aprobar(id, aprobada, nota);
      setMensajes((previos) =>
        previos.map((m) =>
          m.aprobaciones?.some((x) => x.id === id)
            ? {
                ...m,
                aprobaciones: m.aprobaciones.map((x) =>
                  x.id === id ? { ...x, estado: aprobada ? "granted" : "denied" } : x,
                ),
              }
            : m,
        ),
      );
    } catch (error) {
      await avisar(
        error instanceof ApiError ? error.message : "No se pudo decidir. Prueba otra vez.",
      );
    } finally {
      setDecidiendo(null);
    }
  }

  async function enviarTexto(crudo: string) {
    const texto = crudo.trim();
    if (!texto || !conversacionId || respondiendo) return;

    // Lo que pida el mensaje manda sobre el interruptor, y solo por este turno:
    // "contéstame en voz" es más rápido que buscar el selector.
    /**
     * Con auriculares se lee; escribiendo o con el dedo, no.
     *
     * Lo que se pida dentro del propio mensaje manda por encima: si dices
     * «léemelo» mirando la pantalla, se lee.
     */
    const modoDeEsteTurno: ModoDeRespuesta =
      modoPedido(texto) ?? (manosLibresPuesto ? "ambas" : "texto");

    setEntrada("");
    setError(null);
    setRespondiendo(true);
    setPensando(true);

    const conmigo = adjuntos;
    setAdjuntos([]);
    const idMio = `local-${Date.now()}`;
    setMensajes((previos) => [
      ...previos,
      { id: idMio, role: "user", content: texto },
      { id: "respondiendo", role: "assistant", content: "" },
    ]);

    /**
     * Tu mensaje sube arriba del todo y la respuesta se escribe por debajo.
     *
     * Antes esto perseguía el final del hilo, así que la respuesta aparecía
     * pegada al borde de abajo y para leerla desde el principio había que subir
     * a mano. Leer es de arriba abajo: se coloca el principio arriba una vez y
     * ya no se toca más, que es como se lee cualquier otra cosa.
     *
     * En el fotograma siguiente, cuando el mensaje ya existe en la pantalla.
     */
    requestAnimationFrame(() => {
      arribaDelTodo.current = idMio;
      document.getElementById(idMio)?.scrollIntoView({ block: "start", behavior: "smooth" });
    });

    const id = conversacionId;

    const controlador = new AbortController();
    corte.current = controlador;
    ultimaLetra.current = Date.now();

    await api.enviar(id, texto, {
      onThinking: () => setPensando(true),
      onHerramienta: (nombre) => {
        setPensando(true);
        setHaciendo(enCristiano(nombre));
      },
      onText: (fragmento) => {
        setPensando(false);
        setHaciendo(null);
        ultimaLetra.current = Date.now();
        // Se va pintando conforme llega: es lo que hace que no parezca colgado.
        // Pero agrupado por fotograma, no letra a letra.
        porPintar.current += fragmento;
        if (pintadoPendiente.current) return;
        pintadoPendiente.current = true;

        requestAnimationFrame(() => {
          pintadoPendiente.current = false;
          const trozo = porPintar.current;
          porPintar.current = "";
          if (!trozo) return;
          setMensajes((previos) =>
            previos.map((m) =>
              m.id === "respondiendo" ? { ...m, content: m.content + trozo } : m,
            ),
          );
        });
      },
      onAprobacion: (aprobacion) => {
        /*
         * La tarjeta se cuelga del mensaje EN CURSO, en cuanto la herramienta
         * la crea: Jorge decide donde está el contexto, sin ir a otra pestaña.
         *
         * Si ya está en un mensaje de antes, es que ha cambiado —una tarjeta
         * sustituida por otra de la misma web—: se voltea donde está.
         */
        setMensajes((previos) => {
          const deAntes = previos.some(
            (m) => m.id !== "respondiendo" && m.aprobaciones?.some((x) => x.id === aprobacion.id),
          );
          return previos.map((m) =>
            deAntes
              ? m.id !== "respondiendo" && m.aprobaciones?.some((x) => x.id === aprobacion.id)
                ? { ...m, aprobaciones: m.aprobaciones.map((x) => (x.id === aprobacion.id ? aprobacion : x)) }
                : m
              : m.id === "respondiendo"
                ? {
                    ...m,
                    aprobaciones: [
                      ...(m.aprobaciones ?? []).filter((x) => x.id !== aprobacion.id),
                      aprobacion,
                    ],
                  }
                : m,
          );
        });
      },
      onDone: ({ title, pie, cierre }) => {
        let dicho = "";
        // Lo que quedara en el buffer entra ahora: si no, la última palabra se
        // pierde, y con ella el final de lo que se lee en voz alta.
        const resto = porPintar.current;
        porPintar.current = "";

        setMensajes((previos) =>
          previos.map((m) => {
            if (m.id !== "respondiendo") return m;
            dicho = m.content + resto;
            return {
              ...m,
              content: dicho,
              id: `msg-${Date.now()}`,
              ...(pie ? { pie } : {}),
              cierre: cierre ?? null,
            };
          }),
        );

        // Se lee cuando la respuesta está entera. Leerla a trozos según llega
        // sonaría entrecortada, y además el texto se reescribe mientras se
        // escribe: la voz iría siempre a destiempo.
        if (tocaHablar(modoDeEsteTurno) && dicho) void leer(dicho);
        // La conversación sube al principio con la fecha de ahora: si no, se
        // quedaría en "Ayer" mientras estás escribiendo en ella.
        setConversaciones((previas) => {
          const actualizada = previas.map((c) =>
            c.id === id
              ? {
                  ...c,
                  title: title || c.title,
                  updatedAt: new Date().toISOString(),
                  mensajes: c.mensajes + 2,
                }
              : c,
          );
          const esta = actualizada.find((c) => c.id === id);
          return esta ? [esta, ...actualizada.filter((c) => c.id !== id)] : actualizada;
        });
        setRespondiendo(false);
        setPensando(false);
      },
      onTitulo: (nuevo) => {
        setConversaciones((previas) =>
          previas.map((c) => (c.id === id ? { ...c, title: nuevo } : c)),
        );
      },
      onError: (mensaje) => {
        // Si lo has parado tú, no es un error que enseñar.
        if (controlador.signal.aborted) return;
        setError(mensaje);
        porPintar.current = "";
        setMensajes((previos) => previos.filter((m) => m.id !== "respondiendo"));
        setRespondiendo(false);
        setPensando(false);
      },
      },
      /**
       * Con auriculares va por el canal de voz, y eso cambia cómo contesta.
       *
       * El prompt ya sabe que una respuesta hablada se escribe distinta: frases
       * cortas, sin listas ni tablas, tres o cuatro como mucho. Antes esto iba
       * siempre como texto, así que con el móvil en el bolsillo te leía quince
       * líneas con viñetas — que ni se oyen bien ni hacen falta.
       *
       * Ahorra por partida doble: menos tokens de salida y menos caracteres de
       * voz, que se cobra por carácter. Y encima se escucha mejor.
       */
      manosLibresPuesto ? "voice" : "text",
      controlador.signal,
      conmigo,
    ).catch((error: unknown) => {
      // Abortar hace que `fetch` lance. Lo has pedido tú: no hay nada que contar.
      if (controlador.signal.aborted) return;
      throw error;
    });

    corte.current = null;
  }

  return (
    <div className="espacio">
      {dialogo}
      {/* En el móvil el panel va por encima: este velo lo cierra al tocar
          fuera, que es como se sale de cualquier panel en un teléfono. */}
      {historialAbierto && <div className="velo" onClick={cerrarHistorial} />}

      <aside className={`lateral ${historialAbierto ? "abierto" : ""}`}>
        <button className="nueva" onClick={nueva} disabled={respondiendo}>
          Nueva conversación
        </button>
        <nav>
          {agrupar(conversaciones, (c) => c.updatedAt).map(({ grupo, elementos }) => (
            <section key={grupo}>
              <h3 className="grupo">{grupo}</h3>
              {elementos.map((c) =>
                renombrando === c.id ? (
                  <form key={c.id} className="entrada-lista renombrando" onSubmit={guardarNombre}>
                    <input
                      value={nombreNuevo}
                      onChange={(e) => setNombreNuevo(e.target.value)}
                      onBlur={guardarNombre}
                      onKeyDown={(e) => e.key === "Escape" && setRenombrando(null)}
                      maxLength={120}
                      autoFocus
                    />
                  </form>
                ) : (
                  /**
                   * La fila no es un botón: dentro lleva otros botones, y un
                   * botón dentro de otro no es HTML válido — el navegador lo
                   * desmonta y las acciones dejan de responder.
                   */
                  <div
                    key={c.id}
                    className={`entrada-lista ${c.id === conversacionId ? "activa" : ""}`}
                  >
                    <button
                      className="abrir"
                      onClick={() => void abrir(c.id)}
                      disabled={respondiendo}
                      title={c.title}
                    >
                      <span className="titulo">{c.title}</span>
                      <span className="pie">
                        {cuandoBreve(new Date(c.updatedAt))}
                        {c.mensajes > 0 && ` · ${c.mensajes} mensajes`}
                      </span>
                    </button>

                    <div className="acciones">
                      <button
                        onClick={() => empezarARenombrar(c)}
                        disabled={respondiendo}
                        title="Renombrar"
                        aria-label={`Renombrar ${c.title}`}
                      >
                        ✎
                      </button>
                      <button
                        onClick={() => void archivar(c)}
                        disabled={respondiendo}
                        title={viendoArchivadas ? "Devolver a la lista" : "Archivar"}
                        aria-label={`${viendoArchivadas ? "Desarchivar" : "Archivar"} ${c.title}`}
                      >
                        {viendoArchivadas ? "↩" : "▾"}
                      </button>
                      <button
                        className="peligro"
                        onClick={() => void borrar(c)}
                        disabled={respondiendo}
                        title="Borrar para siempre"
                        aria-label={`Borrar ${c.title}`}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ),
              )}
            </section>
          ))}

          {conversaciones.length === 0 && (
            <p className="vacio">
              {viendoArchivadas ? "No hay nada archivado." : "No hay conversaciones."}
            </p>
          )}
        </nav>

        <button className="ver-archivadas" onClick={() => void verArchivadas(!viendoArchivadas)}>
          {viendoArchivadas ? "← Conversaciones" : "Ver archivadas"}
        </button>
      </aside>

      <div className="chat">
        <div className="hilo" ref={hiloRef} onScroll={alDesplazar}>
          {mensajes.length === 0 && !respondiendo && (
            <div className="bienvenida">
              <span className="orbe grande" aria-hidden="true" />
              <h2>Hola, {usuario.displayName}.</h2>
              {estado ? (
                <>
                  <p>
                    Ahora mismo tengo <strong>{estado.clientes} clientes</strong> en la
                    cartera
                    {estado.ordenadores.conectados > 0
                      ? ` y ${estado.ordenadores.conectados} ordenador${
                          estado.ordenadores.conectados === 1 ? "" : "es"
                        } listo${estado.ordenadores.conectados === 1 ? "" : "s"} para trabajar`
                      : ", pero ningún ordenador conectado: puedo pensar y planificar, ejecutar no"}
                    .
                  </p>
                  <ul className="con-que-cuento">
                    {[
                      ["Holded", estado.integraciones.holded],
                      ["Trello", estado.integraciones.trello],
                      ["Correo", estado.integraciones.correo],
                      ["Voz", estado.integraciones.voz],
                    ].map(([nombre, hay]) => (
                      <li key={String(nombre)} className={hay ? "hay" : "falta"}>
                        {String(nombre)}
                      </li>
                    ))}
                  </ul>
                  <p className="pista">Pregúntame lo que quieras, o mantén pulsado el micrófono.</p>
                </>
              ) : (
                <p>Pregúntame lo que quieras.</p>
              )}
            </div>
          )}

          {mensajes.map((mensaje) => (
            <div key={mensaje.id} id={mensaje.id} className={`mensaje ${mensaje.role}`}>
              {/* El orbe identifica quién habla sin necesidad de leer. En una
                  conversación larga eso es lo que deja recorrerla de un vistazo. */}
              {mensaje.role === "assistant" && <span className="orbe" aria-hidden="true" />}

              {/**
                * La burbuja y lo que cuelga de ella, en columna.
                *
                * `.mensaje` es una fila, así que los recuerdos y los botones de
                * copiar y escuchar entraban como DOS COLUMNAS MÁS al lado del
                * texto. En el portátil sobraba sitio y no se notaba; en el móvil
                * dejaba la respuesta en una tira de tres palabras por línea.
                */}
              <div className="columna-mensaje">
              <div className="burbuja">
                {mensaje.role === "assistant" && (
                  <div className="quien">
                    <span className="nombre">BILBOBOT</span>
                    {mensaje.createdAt && (
                      <span className="hora">
                        {new Date(mensaje.createdAt).toLocaleTimeString("es-ES", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    )}
                  </div>
                )}
                {mensaje.content ? (
                  mensaje.role === "assistant" ? (
                    /**
                     * El director escribe en markdown —tablas, listas, negrita—
                     * y hasta ahora se veía en crudo, con los asteriscos.
                     *
                     * `dangerouslySetInnerHTML` con contenido que puede venir
                     * de una tarjeta o un correo daría miedo con razón, pero
                     * `markdownAHtml` escapa TODO el HTML antes de transformar
                     * nada: cuando genera etiquetas ya no queda ningún `<` que
                     * pueda ser parte de una. Ver sus tests de seguridad.
                     */
                    <div
                      id={`msg-${mensaje.id}`}
                      className="formato"
                      dangerouslySetInnerHTML={{ __html: markdownAHtml(mensaje.content) }}
                    />
                  ) : mensaje.role === "system" ? (
                    <AvisoDelSistema contenido={mensaje.content} />
                  ) : (
                    mensaje.content
                  )
                ) : pensando && mensaje.id === "respondiendo" ? (
                  <span className="pensando">
                    {enMayuscula(haciendo ?? "pensando")}… <span className="ico">🧠</span>
                    {/* Los segundos aparecen a los tres, no antes: en una
                        respuesta rápida el contador sería un parpadeo. */}
                    {segundos >= 3 && <span className="cuanto">{segundos} s</span>}
                  </span>
                ) : null}
              </div>

              {/* Lo que iba adjunto. Se queda a la vista para poder volver a
                  ese mensaje dentro de un mes y saber con qué se contestó. */}
              {(mensaje.adjuntos?.length ?? 0) > 0 && (
                <div className="adjuntos-enviados">
                  {mensaje.adjuntos!.map((a) => (
                    <span key={a.id} className="ficha">
                      <span aria-hidden="true">{a.clase === "imagen" ? "🖼" : "📄"}</span>{" "}
                      {a.nombre}
                    </span>
                  ))}
                </div>
              )}

              {/**
               * Con qué recuerdos respondió.
               *
               * Lo pidió el propio director: sin esto ves la respuesta mala
               * pero no el recuerdo malo que la causó, así que lo único que
               * puedes hacer es repetirle la explicación. Con esto vas a la
               * ficha y la arreglas de una vez.
               */}
              {mensaje.role === "assistant" && (mensaje.recuerdos?.length ?? 0) > 0 && (
                <div className="recuerdos-usados">
                  <span className="k">Con qué respondió</span>
                  {mensaje.recuerdos!.map((r) => (
                    <span
                      key={(r.id ?? "") + r.titulo}
                      className={r.como === "guardado" ? "recuerdo-chip nuevo" : "recuerdo-chip"}
                      title={
                        r.como === "guardado"
                          ? "Lo acaba de guardar. Si no es así, díselo ahora."
                          : "Lo tenía guardado y lo ha usado para responder."
                      }
                    >
                      {r.como === "guardado" && <span aria-hidden="true">＋</span>}
                      {r.titulo}
                    </span>
                  ))}
                </div>
              )}

              {/**
               * Lo que espera tu sí o tu no, AQUÍ, donde está el contexto.
               *
               * Lo pidió Jorge: aprobar desde el chat en vez de ir a la
               * pestaña Hoy. La tarjeta enseña lo mismo que el panel —qué va
               * a pasar, por qué, cómo se deshace— y al decidir se voltea.
               * Hoy queda como registro; la decisión se toma aquí.
               */}
              {mensaje.role === "assistant" &&
                (mensaje.aprobaciones?.length ?? 0) > 0 &&
                mensaje.aprobaciones!.map((a) => (
                  <div key={a.id} className="aprobacion en-chat">
                    <div className="t">{a.titulo}</div>
                    <p className="que-pasa">{a.queVaAPasar}</p>
                    {a.trabajos && <DetalleDeTrabajos trabajos={a.trabajos} />}
                    <dl className="detalle">
                      {a.porQue && (
                        <>
                          <dt>Por qué</dt>
                          <dd>{a.porQue}</dd>
                        </>
                      )}
                      {a.explicacionDelRiesgo && (
                        <>
                          <dt>Riesgo</dt>
                          <dd>{a.explicacionDelRiesgo}</dd>
                        </>
                      )}
                      {a.comoSeVuelveAtras && (
                        <>
                          <dt>Si sale mal</dt>
                          <dd>{a.comoSeVuelveAtras}</dd>
                        </>
                      )}
                      {a.recomendacion && (
                        <>
                          <dt>Recomiendo</dt>
                          <dd>{a.recomendacion}</dd>
                        </>
                      )}
                    </dl>
                    {a.estado === "pending" ? (
                      <div className="botones">
                        <button
                          className="acc"
                          disabled={decidiendo === a.id}
                          onClick={() => void decidirAprobacion(a.id, true)}
                        >
                          Aprobar
                        </button>
                        <button
                          className="acc sec"
                          disabled={decidiendo === a.id}
                          onClick={() => void decidirAprobacion(a.id, false)}
                        >
                          No hacerlo
                        </button>
                      </div>
                    ) : (
                      <div className="botones">
                        <span
                          className={
                            a.estado === "granted" ? "pill ok" : a.estado === "expired" ? "pill" : "pill mal"
                          }
                        >
                          {a.estado === "granted"
                            ? "Aprobado — en marcha; te llegará el resultado"
                            : a.estado === "expired"
                              ? a.sustituidaPor
                                ? "Sustituida por la tarjeta nueva, con todo dentro — decide en ésa"
                                : "Caducada sin decidir — no se ha tocado nada"
                              : "Denegado — no se ha tocado nada"}
                        </span>
                      </div>
                    )}
                  </div>
                ))}

              {/**
                * Qué se puede hacer con una respuesta.
                *
                * Copiar es lo más pedido: casi todo lo que escribe acaba pegado
                * en WordPress, en un correo o en una tarjeta. Y escuchar existía
                * pero con `opacity: 0` hasta pasar el ratón — o sea, invisible
                * en el móvil, que es donde más falta hace oír una respuesta
                * larga. Ahora se ven siempre.
                */}
              {/* Cómo acabó y con qué: la pastilla del ESTADO y el pie. */}
              {mensaje.role === "assistant" &&
                mensaje.id !== "respondiendo" &&
                (mensaje.pie || mensaje.cierre) && (
                  <PieDeRespuesta pie={mensaje.pie} cierre={mensaje.cierre ?? null} />
                )}
              {mensaje.role === "assistant" && mensaje.content && mensaje.id !== "respondiendo" && (
                <div className="acciones-mensaje">
                  <button
                    onClick={() => void copiar(mensaje.id, mensaje.content)}
                    title="Copiar esta respuesta"
                    aria-label="Copiar esta respuesta"
                  >
                    {copiado === mensaje.id ? "Copiado" : "Copiar"}
                  </button>

                  <button
                    onClick={() => escuchar(mensaje.id, mensaje.content)}
                    title={leyendo === mensaje.id ? "Parar la lectura" : "Escuchar esta respuesta"}
                    aria-label={
                      leyendo === mensaje.id ? "Parar la lectura" : "Escuchar esta respuesta"
                    }
                  >
                    {leyendo === mensaje.id ? "Parar" : "Escuchar"}
                  </button>
                </div>
              )}
              </div>
            </div>
          ))}

          {error && <div className="aviso">{error}</div>}

          {/**
            * Sitio en blanco al final del hilo.
            *
            * Sin esto, el último mensaje no PUEDE subir arriba del todo: no hay
            * nada por debajo contra lo que desplazarse y el navegador lo deja
            * donde estaba. Es el hueco el que permite leer de arriba abajo.
            */}
          <div className="hueco-lectura" aria-hidden="true" />
          <div ref={finRef} />
        </div>

        <form className="redaccion" onSubmit={enviar}>
          {grabacion && (
            <div className="grabando-aviso">
              <span className="punto-rojo" />
              <span>
                Grabando {segundosGrabando}s ·{" "}
                {manosLibresPuesto ? "calla un momento y se manda" : "suelta para enviar"}
              </span>
              {/* La barra se mueve al hablar: es lo único que confirma que el
                  micrófono está captando algo antes de soltar. */}
              <span className="nivel" aria-hidden="true">
                <span style={{ width: `${Math.min(100, Math.round(nivel * 180))}%` }} />
              </span>
            </div>
          )}

          {/**
           * Todo dentro de una sola caja.
           *
           * Antes eran tres bloques sueltos —modos, caja, Enviar— y parecían
           * tres cosas distintas puestas en fila. Encima la barra de
           * desplazamiento del texto asomaba dentro y aplastaba el micrófono.
           *
           * Ahora es un recuadro único: se escribe arriba y los controles van
           * dentro, abajo. Es la misma forma en el portátil y en el móvil, así
           * que el ancho ya no obliga a reordenar nada.
           */}
          {/* Ir al principio y al final. El de bajar sólo si me he movido:
              ofrecer "ir al final" cuando ya estás al final es ruido. */}
          {mensajes.length > 3 && (
            <div className="saltos">
              <button onClick={irAlPrincipio} title="Ir al principio" aria-label="Ir al principio">
                ↑
              </button>
              {heSubido && (
                <button onClick={irAlFinal} title="Ir al final" aria-label="Ir al final">
                  ↓
                </button>
              )}
            </div>
          )}

          <div className={`compositor ${grabacion ? "escuchando" : ""}`}>
            <textarea
              value={entrada}
              onChange={(e) => setEntrada(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void enviarTexto(entrada);
                }
              }}
              rows={1}
              ref={(el) => {
                if (!el) return;
                /**
                 * Crece con lo escrito hasta un tope, y solo entonces aparece
                 * la barra de desplazamiento.
                 *
                 * Con `overflow: auto` fijo, el navegador pintaba la barra con
                 * una sola línea de texto: metida dentro de la caja y encima
                 * del micrófono.
                 */
                el.style.height = "auto";
                const tope = 168;
                el.style.height = `${Math.min(el.scrollHeight, tope)}px`;
                el.style.overflowY = el.scrollHeight > tope ? "auto" : "hidden";
              }}
              placeholder={
                grabacion
                  ? "Te escucho…"
                  : transcribiendo
                    ? "Transcribiendo…"
                    : conversacionId
                      ? "Escribe o mantén pulsado el micrófono…"
                      : "Preparando…"
              }
              disabled={!conversacionId || respondiendo || transcribiendo}
              autoFocus
            />

            {/**
              * Lo que se va a mandar con el mensaje, antes de mandarlo.
              *
              * Se ve y se puede quitar. Un adjunto que se sube y desaparece de
              * la vista obliga a mandar el mensaje para saber qué llevaba.
              */}
            {adjuntos.length > 0 && (
              <div className="adjuntos">
                {adjuntos.map((a) => (
                  <span key={a.id} className={`ficha ${a.clase}`}>
                    <span className="ico" aria-hidden="true">
                      {a.clase === "imagen" ? "🖼" : "📄"}
                    </span>
                    <span className="nombre" title={a.nombre}>
                      {a.nombre}
                    </span>
                    <span className="peso">{Math.max(1, Math.round(a.bytes / 1024))} KB</span>
                    <button
                      type="button"
                      className="quitar"
                      onClick={() => setAdjuntos((p) => p.filter((x) => x.id !== a.id))}
                      aria-label={`Quitar ${a.nombre}`}
                      title="Quitar"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="controles">
              {/**
                * Aquí había un selector de Texto / Voz / Las dos, y era mentira.
                *
                * `tocaEscribir` no lo llamaba nadie: el texto SIEMPRE ha salido
                * en el chat, así que «Voz» y «Las dos» hacían lo mismo. Tres
                * botones para dos comportamientos, uno de ellos imposible.
                *
                * Y encima había que acordarse de moverlo. Lo que decide si
                * conviene oír la respuesta es CÓMO mandaste el mensaje, y eso ya
                * se sabe sin preguntar:
                *
                *   · Con los auriculares → la lee. El móvil está en el bolsillo;
                *     si no la lee, no te enteras.
                *   · Escribiendo o con el micrófono a dedo → no la lee. Estás
                *     mirando la pantalla, y ahí leer es más rápido que escuchar.
                *
                * Para oír una respuesta suelta está el botón «Escuchar» de cada
                * mensaje, que es donde uno lo busca cuando le hace falta.
                */}

              <div className="enviar">
                {/**
                  * Adjuntar. Imágenes y documentos, no vídeo.
                  *
                  * El `accept` no es cosmético: en el móvil decide qué te ofrece
                  * el sistema al pulsar. Sin él saldría la galería entera con los
                  * vídeos incluidos, y elegir uno acabaría en un rechazo — culpa
                  * de la interfaz por haberlo ofrecido, no de quien lo eligió.
                  */}
                <input
                  ref={selector}
                  type="file"
                  multiple
                  accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,text/plain,text/markdown,text/csv,application/json"
                  style={{ display: "none" }}
                  onChange={(e) => void elegirFicheros(e.target.files)}
                />
                <button
                  type="button"
                  className="adjuntar"
                  onClick={() => selector.current?.click()}
                  disabled={!conversacionId || respondiendo || subiendo || adjuntos.length >= 5}
                  title={
                    adjuntos.length >= 5
                      ? "Cinco ficheros por mensaje es el tope"
                      : "Adjuntar una imagen o un documento (PDF, texto)"
                  }
                  aria-label="Adjuntar un fichero"
                >
                  {subiendo ? "…" : "📎"}
                </button>

                {/**
                  * Manos libres: el botón de los auriculares en vez del dedo.
                  *
                  * Se enciende a mano y no está siempre puesto porque mientras
                  * lo está, ese botón deja de controlar la música. Secuestrar
                  * el mando de alguien sin que lo haya pedido es de las cosas
                  * que más molestan y menos se entienden.
                  */}
                {/**
                  * Conversar: como una llamada, hasta que cuelgues.
                  *
                  * Separado del manos libres a propósito, aunque los dos usen la
                  * voz. Aquél es «hablo yo cuando quiero»; éste es «estamos
                  * hablando». Uno se puede tener puesto todo el día; el otro
                  * tiene el micrófono abierto y va gastando, así que se enciende
                  * para algo concreto y se cuelga al terminar.
                  */}
                {sePuedeConversar() && (
                  <button
                    type="button"
                    className={`charlar ${enCharla ? "en-marcha" : ""}`}
                    onClick={() => (enCharla ? colgarCharla() : void empezarCharla())}
                    disabled={!conversacionId || dictadoDisponible === false}
                    title={
                      enCharla
                        ? `Conversación en marcha · ${turnosDeCharla} intercambios · ${gastoDeCharla.toFixed(1)} cént. Pulsa para colgar.`
                        : "Conversar por voz: habla y te contesta hablando, hasta que cuelgues"
                    }
                    aria-pressed={enCharla}
                    aria-label="Conversar por voz"
                  >
                    {enCharla ? "Colgar" : "💬"}
                  </button>
                )}

                {sePuedeGrabar() && sePuedeManosLibres() && (
                  <button
                    type="button"
                    className={`manos-libres ${manosLibresPuesto ? "puesto" : ""}`}
                    onClick={() =>
                      manosLibresPuesto ? apagarManosLibres() : void encenderManosLibres()
                    }
                    disabled={!conversacionId || dictadoDisponible === false}
                    title={
                      manosLibresPuesto
                        ? "Manos libres encendido: pellizca el auricular para hablar y calla un momento para mandarlo. Mientras tanto el botón no controla la música."
                        : "Hablarle desde el botón de los auriculares, sin tocar la pantalla"
                    }
                    aria-pressed={manosLibresPuesto}
                    aria-label="Manos libres con los auriculares"
                  >
                    🎧
                  </button>
                )}

                {sePuedeGrabar() && (
                  /**
                   * Mantener pulsado para hablar, como en WhatsApp.
                   *
                   * Con eventos de puntero, que valen para dedo y para ratón
                   * sin escribir las dos cosas. `onPointerLeave` cancela:
                   * sacar el dedo del botón es la forma de arrepentirse sin
                   * enviar nada, y no hace falta explicarla porque ya la
                   * conoce todo el mundo.
                   */
                  <button
                    type="button"
                    className={`microfono ${grabacion ? "grabando" : ""}`}
                    onPointerDown={(e) => {
                      // Sin esto, en el móvil la pulsación larga se entiende
                      // como "seleccionar texto" y sale la lupa encima.
                      e.preventDefault();
                      void empezarAGrabar();
                    }}
                    onPointerUp={() => void soltarMicrofono(false)}
                    onPointerLeave={() => void soltarMicrofono(true)}
                    onContextMenu={(e) => e.preventDefault()}
                    disabled={
                      !conversacionId ||
                      respondiendo ||
                      transcribiendo ||
                      dictadoDisponible === false
                    }
                    title={
                      dictadoDisponible === false
                        ? "El dictado necesita una clave de OpenAI en el servidor"
                        : "Mantén pulsado para hablar"
                    }
                    aria-label="Mantén pulsado para grabar una nota de voz"
                  >
                    {/**
                     * Micrófono dibujado, no el emoji 🎙.
                     *
                     * El emoji lo pinta cada sistema a su manera —en Windows
                     * sale gris y diminuto, en Android en color— y encima
                     * cambia de tamaño según la fuente. Un SVG mide siempre lo
                     * mismo y toma el color del botón, que es lo que hace
                     * falta para que se vea rojo al grabar.
                     */}
                    <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true">
                      {grabacion ? (
                        <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
                      ) : (
                        <>
                          <rect
                            x="9"
                            y="3"
                            width="6"
                            height="11"
                            rx="3"
                            fill="currentColor"
                          />
                          <path
                            d="M5 11a7 7 0 0 0 14 0"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                          <path
                            d="M12 18v3"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </>
                      )}
                    </svg>
                  </button>
                )}

                {/**
                  * El mismo sitio para enviar y para parar.
                  *
                  * Mientras contesta, el botón de enviar no sirve de nada —está
                  * apagado— así que ese hueco es exactamente donde la mano ya
                  * está cuando te das cuenta de que has mandado algo mal. Un
                  * botón de parar en otro sitio habría que buscarlo.
                  */}
                {respondiendo ? (
                  <button
                    type="button"
                    className="parar"
                    onClick={parar}
                    aria-label="Parar la respuesta"
                    title="Parar"
                  >
                    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
                      <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
                    </svg>
                    Parar
                  </button>
                ) : (
                  <button type="submit" disabled={!entrada.trim()}>
                    Enviar
                  </button>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
